import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useRef, type ComponentProps } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { FullViewportScroll } from '@sorare/core/src/atoms/container/FullViewportScroll';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { WithScrollButtons } from '@sorare/core/src/atoms/navigation/WithScrollButtons';
import { BodyM } from '@sorare/core/src/atoms/typography';
import {
  mobileAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import { HeaderSection } from '#src/components/market-home/HeaderSection/index.tsx';
import { WatchlistItem } from '#src/components/watchlists/WatchlistItem/index.tsx';

import type { PopularWatchlists_watchlist } from './__generated__/index.graphql.ts';

type Props = {
  watchlists: PopularWatchlists_watchlist[];
  track?: ComponentProps<typeof WatchlistItem>['track'];
};

const StyledFullViewportScroll = styled(FullViewportScroll)`
  @media ${tabletAndAbove} {
    mask-image: linear-gradient(
      to right,
      transparent,
      transparent calc(var(--container-padding) / 2),
      black var(--container-padding),
      black calc(100% - var(--container-padding)),
      transparent calc(100% - var(--container-padding) / 2),
      transparent 100%
    );
  }
`;

const FlexStartHorizontal = styled(Horizontal).attrs({
  center: false,
  gap: 2,
})`
  align-items: flex-start;
  justify-content: flex-start;
`;

const WatchlistItemWrapper = styled.div`
  height: 100%;
  min-width: 300px;
  @media ${mobileAndAbove} {
    width: 320px;
  }
`;

const BoldBodyM = styled(BodyM).attrs({ bold: true })``;

export const PopularWatchlists = ({ watchlists, track }: Props) => {
  const wrapperRef = useRef(null);
  const scrollContainerRef = useRef(null);
  if (watchlists.length === 0) return null;
  return (
    <Vertical gap={3}>
      <HeaderSection
        title={<FormattedMessage defaultMessage="Trending Watchlists" />}
      />
      <WithScrollButtons
        wrapperRef={wrapperRef}
        scrollContainerRef={scrollContainerRef}
        contained
      >
        <StyledFullViewportScroll ref={scrollContainerRef}>
          <FlexStartHorizontal>
            {watchlists?.map(w => {
              return (
                <WatchlistItemWrapper key={w.slug}>
                  <WatchlistItem
                    key={w.slug}
                    watchlist={w}
                    WatchlistTitle={BoldBodyM}
                    track={track}
                  />
                </WatchlistItemWrapper>
              );
            })}
          </FlexStartHorizontal>
        </StyledFullViewportScroll>
      </WithScrollButtons>
    </Vertical>
  );
};
PopularWatchlists.fragments = {
  watchlist: gql`
    fragment PopularWatchlists_watchlist on Watchlist {
      slug
      ...WatchlistItem_watchlist
    }
    ${WatchlistItem.fragments.watchlist}
  ` as TypedDocumentNode<PopularWatchlists_watchlist>,
};
